import { createContext, FC, useContext } from 'react'
import { ClickerCommand } from './clicker'

/**
 * Clicker presses that arrive from elsewhere than this window's keyboard -
 * presenter mode's presentation window, which has the focus while it is full
 * screen, sends its presses to the console. `useClicker` takes them as if
 * they had been pressed here.
 */
export type ClickerPresses = {
	subscribe: (listener: (command: ClickerCommand) => void) => () => void
}

export type ClickerPressSource = ClickerPresses & {
	press: (command: ClickerCommand) => void
}

export const createClickerPresses = (): ClickerPressSource => {
	const listeners = new Set<(command: ClickerCommand) => void>()
	return {
		subscribe: listener => {
			listeners.add(listener)
			return () => listeners.delete(listener)
		},
		press: command => listeners.forEach(listener => listener(command)),
	}
}

const ClickerPressesContext = createContext<ClickerPresses | null>(null)

export const ProvideClickerPresses: FC<{ presses: ClickerPresses }> = ({
	presses,
	children,
}) => (
	<ClickerPressesContext.Provider value={presses}>
		{children}
	</ClickerPressesContext.Provider>
)

export const useClickerPresses = (): ClickerPresses | null =>
	useContext(ClickerPressesContext)
